import { Component, ElementRef, HostListener, ViewChild, inject, input, output, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface OptionPickerOption<T extends string | number = string> {
  value: T;
  label: string;
  /** Secondary line rendered beneath the label; leave unset for a single-line entry. */
  description?: string;
  /** Inline SVG markup rendered to the left of the label. */
  icon?: string;
  /** Renders the entry in the danger color for destructive actions. */
  danger?: boolean;
}

@Component({
  selector: 'app-option-picker',
  standalone: true,
  templateUrl: './option-picker.html',
  styleUrl: './option-picker.scss',
})
export class OptionPickerComponent<T extends string | number = string> {
  readonly options = input.required<ReadonlyArray<OptionPickerOption<T>>>();
  /** Value to mark as selected; omit for menus that have no persistent selection. */
  readonly selectedValue = input<T>();
  readonly placeholder = input('Select');
  readonly placement = input<'up' | 'down'>('up');
  readonly ariaLabel = input('Options');
  /** When set, the trigger renders as an icon-only circular button instead of a label pill. */
  readonly triggerIconSvg = input<string>();
  readonly valueChange = output<T>();

  private readonly sanitizer = inject(DomSanitizer);

  protected readonly isOpen = signal(false);
  @ViewChild('picker') private readonly picker: ElementRef<HTMLElement> | undefined;

  /** Marks the trigger SVG as trusted so it isn't stripped by the sanitizer. */
  protected triggerIconHtml(): SafeHtml | undefined {
    const value = this.triggerIconSvg();
    return value ? this.sanitizer.bypassSecurityTrustHtml(value) : undefined;
  }

  /** Marks an option SVG as trusted so it isn't stripped by the sanitizer. */
  protected optionIconHtml(icon?: string): SafeHtml | undefined {
    return icon ? this.sanitizer.bypassSecurityTrustHtml(icon) : undefined;
  }

  protected selectedOption(): OptionPickerOption<T> | undefined {
    return this.options().find((option) => option.value === this.selectedValue());
  }

  protected triggerLabel(): string {
    return this.selectedOption()?.label ?? this.placeholder();
  }

  protected selectOption(value: T): void {
    this.valueChange.emit(value);
    this.isOpen.set(false);
  }

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  @HostListener('document:keydown.escape')
  protected closeOnEscape(): void {
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected closeOnClickOutside(event: MouseEvent): void {
    if (!this.isOpen()) {
      return;
    }
    const root = this.picker?.nativeElement;
    if (root && event.target instanceof Node && root.contains(event.target)) {
      return;
    }
    this.isOpen.set(false);
  }
}
